import { Link } from "react-router";
import { readJson } from "../lib/content.server.js";
import { breadcrumbJsonLd, buildMeta } from "../lib/seo.js";
import { SITE } from "../lib/site.js";
import Breadcrumbs from "../components/Breadcrumbs/Breadcrumbs.jsx";
import Picture, { CARD } from "../components/Picture/Picture.jsx";
import LoopVideo from "../components/LoopVideo/LoopVideo.jsx";
import ScanStory from "../components/ScanStory/ScanStory.jsx";
import AnalysisScene from "../components/AnalysisScene/AnalysisScene.jsx";
import { ORDER as ANALYSIS_ORDER } from "../components/AnalysisScene/steps.js";
import SectionHeader from "../components/SectionHeader/SectionHeader.jsx";
import CTABand from "../components/CTABand/CTABand.jsx";
import styles from "./bimlive.module.css";
import { ANALYSES } from "../lib/analyses.js";
import { surfaceFor } from "../lib/surface.js";
import { useTabs } from "../lib/useTabs.js";

const PAGE = {
  title: "123BIMLive",
  description:
    "123BIMLive runs Road Safety Audit 3D in the cloud: upload a LiDAR scan and get stopping, passing and intersection sight distance, curves, clear zones, glare and more, with reports, video and GIS layers.",
  path: "/123bimlive",
};
const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Road Safety Audit 3D", path: "/road-safety-audit-3d" },
  { name: "123BIMLive", path: PAGE.path },
];

export function meta() {
  return buildMeta({
    ...PAGE,
    image: "/images/123bimlive/ssd-960.jpg",
    jsonLd: [breadcrumbJsonLd(CRUMBS)],
  });
}

export async function loader() {
  const dots = readJson("clients").filter((c) => c.type === "dot").length;
  return { dots };
}

// The one switch for this page's light sections (redesign step 3, 2026-09-24): these sit on
// the light "plan sheet" surface from app/styles/surfaces.css. Empty the list to put the
// whole page back on the dark surface; nothing else needs to change.
const LIGHT_SECTIONS = ["benefits", "deliverables", "designs"];
const surface = surfaceFor(LIGHT_SECTIONS);

const BENEFITS = [
  {
    title: "Audit every mile, not a sample",
    text: "A field audit covers the sites someone can be paid to visit. Here every station on the corridor is checked, in both directions, so nothing ends up low priority only because no one drove it.",
  },
  {
    title: "Spend the safety budget where it prevents crashes",
    text: "Findings come ranked by crash risk. The Safety Dashboard tests them against your state's crash records and prices the fix — crashes prevented, annual benefit and benefit-cost ratio, in the form an HSIP application asks for.",
  },
  {
    title: "Nobody stands in live traffic",
    text: "Nothing is measured from the shoulder. No lane closures, no traffic control, no crew exposed to the road you are auditing.",
  },
  {
    title: "Findings that hold up in review",
    text: "Every check follows the AASHTO Green Book, the Roadside Design Guide and the MUTCD, applied the same way at every station, with the driver's view from the scan behind each verdict.",
  },
  {
    title: "One upload, every analysis",
    text: "Sight distance, curves, clear zone, glare, guardrail and the rest all run on the same scan. Adding one later reuses that scan instead of sending anyone back out to collect the data again.",
  },
  {
    title: "Something to show, not just a report",
    text: "Drive-through video, an online report for every road and a 3D viewer in the browser — what a commission meeting, a public hearing or a funding request actually needs.",
  },
];

const STEPS = [
  {
    title: "Upload the scan",
    text: "LAS and LAZ point clouds, multi-gigabyte files included. Uploads resume after a dropped connection, and scans in state plane feet or WGS84 are reprojected to the project's coordinate system automatically.",
  },
  {
    title: "Prepare the point cloud",
    text: "The scan is split into tiles, the ground is classified, a Point Transformer V3 model finds the road surface and parked and passing vehicles are removed.",
  },
  {
    title: "Run the analyses",
    text: "Sight lines are rendered on GPUs station by station, across hundreds of parallel cloud jobs, and an AI model names what blocks each failing view.",
  },
  {
    title: "Deliver the findings",
    text: "Reports, an online report for every road, drive-through video, CSV tables and shapefiles land in the project folder, ready to review.",
  },
];

// The analyses story's steps, in the order the scene's camera drives past them.
const ANALYSIS_STEPS = ANALYSIS_ORDER.map((key) => {
  const a = ANALYSES.find((item) => item.key === key);
  return { key, code: a.code, tint: a.tint, title: a.title, text: a.does, result: a.gets };
});

const DASH_METRICS = [
  ["88", "Crashes in the filter", "on 43.27 mi of selected corridor"],
  ["2.03", "Crashes per mile", "four corridors of SR-102 and SR-23"],
  ["1 / 13 / 73", "Fatal / injury / property damage", "severity split of those crashes"],
  ["12", "Stopping sight distance runs", "0.47 mi — 1.1% of the corridor"],
  ["10%", "Crashes beside a run", "within 0.1 mi, four of them inside one"],
  ["$984K / yr", "Expected savings", "if the priced countermeasures are built"],
];

const DASH_POINTS = [
  {
    title: "Ask one question at a time",
    text: "Filter to an analysis, a direction, a milepost range and the corridors you care about. The map, the counts and the benefit figures all follow the filter.",
  },
  {
    title: "Both layers on one map",
    text: "Deficiency runs drawn by severity, crashes by severity — fatal, injury, property damage, animal strike — plus crash clusters, intersection sight-distance rings and glare-time matches.",
  },
  {
    title: "Milepost by milepost",
    text: "Under the map each corridor breaks down in route order: the crashes, the deficiency runs and the intersections on every stretch.",
  },
  {
    title: "A statistical verdict, not a hunch",
    text: "A permutation test against random placement on the same corridor says whether the crashes really do concentrate at the deficiencies, per analysis.",
  },
  {
    title: "A priced countermeasure program",
    text: "Crash modification factors from the FHWA CMF Clearinghouse and FHWA crash costs give crashes prevented, annual benefit and a benefit-cost ratio for every site — the numbers an HSIP application asks for.",
  },
  {
    title: "Intersection priorities",
    text: "Every junction in the state data ranked by comprehensive crash cost, or by what a countermeasure there would be worth.",
  },
];

const FORMATS = ["PDF", "Word", "MP4", "CSV", "Shapefile", "DXF"];

const DELIVERABLE_GROUPS = [
  {
    title: "Reports you can file",
    media: {
      src: "/images/123bimlive/online-report",
      alt: "The online stopping sight distance report: the LiDAR scan rendered from the driver's eye with map and zoom insets, beside a table of every station's visibility, obstruction, milepost, slope and maximum safe speed",
      ratio: "2549 / 1344",
      caption:
        "The online report: one page per road and direction, every tested point on it (17,274 on this corridor). Pick a row and the scan jumps there: the driver’s eye view, the target it was measured against, a zoom on whatever is in the way and the point on the map.",
    },
    items: [
      ["Summary report", "The corridor's deficiencies, ranked, with the criteria each one failed."],
      [
        "Comprehensive report",
        "Every station of every analysis, the numbers behind each verdict and the standard applied.",
      ],
      ["One per road and direction", "Split the way a project is reviewed, in PDF and in Word."],
    ],
  },
  {
    title: "Show it to people",
    media: {
      src: "/images/site/rsa3d/existing-roads",
      alt: "Sight distance results along an existing road, colored by pass and fail",
      ratio: "16 / 9",
    },
    items: [
      [
        "Drive-through video",
        "The corridor as a driver sees it, findings drawn on the road, per analysis and direction.",
      ],
      ["Glare heat maps", "Where and when the sun sits in the driver's eyes, through the year."],
      [
        "Stills for the slide deck",
        "The driver's view at each deficiency, ready for a commission meeting or a hearing.",
      ],
    ],
  },
  {
    title: "Hand it to the engineers",
    media: {
      src: "/images/site/rsa3d/sight-distance",
      alt: "Sight distance analysis rendered over a 3D model of a rural road",
      ratio: "16 / 9",
    },
    items: [
      [
        "Station tables",
        "CSV of every station: available and required sight distance, speed, grade, radius, superelevation.",
      ],
      [
        "GIS layers",
        "Shapefiles of every eye position, target, sight line and deficiency run — straight into ArcGIS.",
      ],
      ["DXF cross-sections", "Embankment sections cut from the scan, for design to work from."],
      ["Sign inventory", "Every sign georeferenced, with its MUTCD code and size."],
    ],
  },
  {
    title: "Take it to the program",
    media: { program: true },
    items: [
      [
        "Priced countermeasures",
        "Crashes prevented, annual benefit and benefit-cost ratio per site, in the form HSIP asks for.",
      ],
      [
        "A ranked register",
        "Every deficiency with its crash-risk score and what blocks the view, so the worst is the first fixed.",
      ],
    ],
  },
];

const DESIGN_POINTS = [
  {
    title: "Send what your designers already work in",
    text: "Bentley MicroStation and OpenRoads, Autodesk Civil 3D and InfraWorks, SketchUp, or a plain DGN or DWG. We build the 3D model from the design files themselves — nothing has to be redrawn for the audit.",
  },
  {
    title: "The same analyses, the same standards",
    text: "Stopping, passing and intersection sight distance, curves, clear zone and the rest, measured on the design surface against the same AASHTO criteria a scan is held to.",
  },
  {
    title: "Findings while they are still cheap to fix",
    text: "A sight line that fails on the model costs a design revision. The same failure found after construction costs a change order, a retrofit — or a crash record.",
  },
  {
    title: "Alternatives, compared before anyone commits",
    text: "Run each alternative and see which geometry actually drives safely, then take the answer to the design review with the model to show for it.",
  },
];

const PLATFORM = [
  {
    title: "Cloud scale",
    text: "A run fans out across up to 180 parallel jobs a stage, with GPU rendering for visibility. Reruns reuse the prepared scan instead of processing it again.",
  },
  {
    title: "3D viewer in the browser",
    text: "Stream the project in full 3D with saved views, environment settings, layers, animations and virtual tours.",
  },
  {
    title: "Routes on the map",
    text: "Draw paths and lanes over the scan on a map, or build routes from a road and milepost range where state milepost data is connected.",
  },
  {
    title: "Secure sign-in",
    text: "Two-factor sign-in with an authenticator app, and roles that keep each manager and user to their own projects.",
  },
];

/** The priced countermeasure program as a table: the program tab's picture. */
function ProgramTable() {
  return (
    <div className={styles.program}>
      <p className={styles.programHead}>Countermeasure program</p>
      <table>
        <thead>
          <tr>
            <th scope="col">Site</th>
            <th scope="col">Benefit / yr</th>
            <th scope="col">B/C</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>MP 13.46</td>
            <td>$412K</td>
            <td>6.8</td>
          </tr>
          <tr>
            <td>MP 21.07</td>
            <td>$268K</td>
            <td>4.1</td>
          </tr>
          <tr>
            <td>MP 8.92</td>
            <td>$155K</td>
            <td>2.9</td>
          </tr>
        </tbody>
      </table>
      <p className={styles.programFoot}>Illustrative values</p>
    </div>
  );
}

/**
 * The deliverables by who they are for, one tab each, instead of four equal cards: each
 * panel pairs the audience's files with a picture of one of them.
 */
function DeliverableTabs({ groups }) {
  const { tabProps, panelProps } = useTabs(groups.length);
  return (
    <div className={styles.deliver}>
      <div
        className={`segmented ${styles.deliverTabs}`}
        role="tablist"
        aria-label="Deliverables by audience"
      >
        {groups.map((group, i) => (
          <button key={group.title} {...tabProps(i)}>
            {group.title}
          </button>
        ))}
      </div>
      {groups.map((group, i) => (
        <div key={group.title} {...panelProps(i)} className={styles.deliverPanel}>
          <figure className={styles.deliverMedia}>
            {group.media.program ? (
              <ProgramTable />
            ) : (
              <Picture
                src={group.media.src}
                alt={group.media.alt}
                ratio={group.media.ratio}
                widths={[480, 960, 1600]}
                fallbackWidth={960}
                sizes="(min-width: 64rem) 55vw, 100vw"
                className={styles.deliverImage}
              />
            )}
            {group.media.caption ? (
              <figcaption className={styles.deliverCaption}>{group.media.caption}</figcaption>
            ) : null}
          </figure>
          <ul className={styles.deliverList}>
            {group.items.map(([name, text]) => (
              <li key={name}>
                <strong>{name}</strong>
                {text}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default function BimLive({ loaderData }) {
  const { dots } = loaderData;
  return (
    <>
      <section className={styles.hero} aria-labelledby="bimlive-title">
        <div className={`container ${styles.heroGrid}`}>
          <div className={styles.heroText}>
            <Breadcrumbs items={CRUMBS} />
            <h1 id="bimlive-title" className={styles.heroTitle}>
              Upload a <span className={styles.input}>LiDAR</span> scan or a{" "}
              <span className={styles.input}>CAD</span> design. Get a road safety audit.
            </h1>
            <p className={styles.heroLead}>
              {/* Non-breaking hyphens (U+2011) keep "state-of-the-art" on one line. */}
              {"State‑of‑the‑art"} AI audits every mile of your LiDAR scan and connects the hazards
              it finds to your crash records, so you fix what puts drivers at risk first, without
              putting a crew on the&nbsp;road.
            </p>
            <div className={styles.heroActions}>
              <Link to="/demo" className="btn btn-primary">
                Request a demo
              </Link>
              <a href="#analyses" className={styles.heroLink}>
                See the ten analyses
              </a>
            </div>
            <p className={styles.heroNote}>
              LiDAR of a road you already own, or the CAD model of one you are still designing:
              Bentley MicroStation and OpenRoads, Autodesk Civil 3D.{" "}
              <a href="#designs">How a design run works</a>
            </p>
            <p className={styles.heroNote}>
              From RDV Systems, trusted by {dots} state DOTs since {SITE.foundedYear}.
            </p>
          </div>
          <div className={styles.heroMedia}>
            {/* The real product first: the scroll story below is the simulation. */}
            <LoopVideo
              src="/videos/rsa-loop.mp4"
              poster="/images/site/home/rsa-loop.jpg"
              caption="Sight distance checked on a LiDAR scan of an existing road"
              focus="left center"
              ratio="16 / 11"
            />
            <p className={styles.heroChip}>Every station, both directions</p>
          </div>
        </div>
      </section>

      {/* Full-bleed and ten screens tall: the corridor pins while each analysis scrolls past. */}
      <section id="analyses" aria-labelledby="analyses-title">
        <ScanStory
          titleId="analyses-title"
          kicker="Ten analyses, one scan"
          title="Choose what each corridor needs"
          steps={ANALYSIS_STEPS}
          Scene={AnalysisScene}
          noun="Analysis"
          reducedLayout="pinned"
          className={styles.analysesStory}
          action={{ to: "/demo", label: "Request a demo" }}
          note="The corridor is simulated. Every check on it, and every number in the readout, is the analysis's own arithmetic on that geometry, against AASHTO and MUTCD criteria."
        />
      </section>

      <section
        className="section"
        data-surface={surface("benefits")}
        aria-labelledby="benefits-title"
      >
        <div className="container">
          <SectionHeader
            id="benefits-title"
            title="Know where your network puts drivers at risk, and what to fix first"
            aside="123BIMLive turns a LiDAR scan you already own into a safety audit of the whole corridor: what is deficient, what is blocking the view, and which fixes buy the most safety per dollar."
          />
          <ul className={styles.benefitList}>
            {BENEFITS.map((benefit) => (
              <li key={benefit.title} className={styles.benefit}>
                <h3 className={styles.benefitTitle}>{benefit.title}</h3>
                <p>{benefit.text}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section" id="how-it-runs" aria-labelledby="run-title">
        <div className="container">
          <SectionHeader
            id="run-title"
            title="From point cloud to findings, without a workstation"
            aside="One upload runs the whole pipeline in the cloud, on hundreds of parallel jobs."
          />
          <ol className={styles.run}>
            {STEPS.map((step, i) => (
              <li key={step.title}>
                <span className={styles.runNo} aria-hidden="true">
                  {i + 1}
                </span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section
        className="section"
        data-surface={surface("deliverables")}
        aria-labelledby="deliver-title"
      >
        <div className="container">
          <SectionHeader
            id="deliver-title"
            title="One run, the whole package"
            aside="Everything lands in the project folder together — nothing to request, export or chase afterwards."
          />
          <ul className={styles.formats} aria-label="Delivered formats">
            {FORMATS.map((format) => (
              <li key={format}>{format}</li>
            ))}
          </ul>

          <DeliverableTabs groups={DELIVERABLE_GROUPS} />
        </div>
      </section>

      <section className="section" aria-labelledby="safety-title">
        <div className="container">
          <SectionHeader
            id="safety-title"
            title="Connect the findings to the crashes"
            aside="A deficiency is only half the story. The Safety Dashboard puts a corridor’s findings next to your state’s crash records, on the same mileposts, so a run of blocked sight distance becomes a place where drivers are crashing."
          />
          <figure className={styles.dash}>
            <Picture
              src="/images/123bimlive/safety-dashboard-office"
              alt="The Safety Dashboard on a monitor in an office: four corridors on a map with stopping sight distance deficiency runs, crashes by severity, crash clusters and intersection rings, beside a panel of crash counts and expected savings"
              ratio="2776 / 1251"
              widths={[960, 1440, 1920, 2560]}
              fallbackWidth={1440}
              sizes="(min-width: 114.6rem) 110rem, 96vw"
              className={styles.dashImage}
            />
            {/* The screenshot's own figures, docked under it like the home hero's. */}
            <ul
              className={styles.dashMetrics}
              aria-label="What the dashboard shows for this corridor"
            >
              {DASH_METRICS.map(([value, label, note]) => (
                <li key={label}>
                  <p className={styles.metricValue}>{value}</p>
                  <p className={styles.metricLabel}>{label}</p>
                  <p className={styles.metricNote}>{note}</p>
                </li>
              ))}
            </ul>
            <figcaption className={styles.dashCaption}>
              Four corridors of SR-102 and SR-23, stopping sight distance against the state’s
              2021–2026 crash data. Twelve deficiency runs cover 1.1% of the 43.27 selected miles —
              and 10% of the crashes fall within a tenth of a mile of one.
            </figcaption>
          </figure>

          <div className={styles.dashBody}>
            <p className={styles.dashLead}>
              RDV’s safety engineers work it with you: choose the analysis, the direction and the
              corridors, and the dashboard answers whether crashes really concentrate at the
              deficiencies, then prices what fixing them would be worth.
            </p>
            <ul className={styles.dashPoints}>
              {DASH_POINTS.map((point) => (
                <li key={point.title}>
                  <h3>{point.title}</h3>
                  <p>{point.text}</p>
                </li>
              ))}
            </ul>
          </div>
          <p className={styles.dashFoot}>
            Crash data comes from the state’s own dataset, matched to your corridors by milepost.
            One state is connected today; others follow as their data is loaded.
          </p>
        </div>
      </section>

      <section
        className={`section ${styles.designsSection}`}
        data-surface={surface("designs")}
        id="designs"
        aria-labelledby="designs-title"
      >
        <div className={`container ${styles.designs}`}>
          <div className={styles.designsIntro}>
            <h2 id="designs-title" className={styles.designsTitle}>
              No LiDAR? No problem
            </h2>
            <p>
              If the road exists only as a design, the audit still runs. RSA 3D began on CAD models
              — sight distance checked on proposed geometry — and only later learned to read LiDAR
              of roads already built. Both still run on the same platform, against the same
              standards.
            </p>
            <ul className={styles.designsList}>
              {DESIGN_POINTS.map((point) => (
                <li key={point.title}>
                  <h3>{point.title}</h3>
                  <p>{point.text}</p>
                </li>
              ))}
            </ul>
            <p className={styles.designsFoot}>
              A design run hands back the same reports, video and tables, plus a colour-coded CAD
              drawing of the maximum safe speed at every location — in a form the design team can
              take straight back into the model.{" "}
              <Link to="/case-studies/henderson-interchange">
                See it on the Henderson Interchange
              </Link>
              .
            </p>
          </div>
          <figure className={styles.designsMedia}>
            <Picture
              src="/images/site/rsa3d/planning-design"
              alt="A proposed interchange design loaded into Road Safety Audit 3D"
              {...CARD}
              sizes="(min-width: 64rem) 45vw, 100vw"
              className={styles.designsImage}
            />
            <figcaption className={styles.designsCaption}>
              Sight distance checked on a model built from the design files
            </figcaption>
          </figure>
        </div>
      </section>

      <section className="section" aria-labelledby="platform-title">
        <div className="container">
          <SectionHeader
            id="platform-title"
            title="Built for the whole network"
            aside="Analyses follow the AASHTO Green Book (7th edition), the AASHTO Roadside Design Guide and the MUTCD."
          />
          <ul className={styles.platform}>
            {PLATFORM.map((item) => (
              <li key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <CTABand
        variant="statement"
        image="/images/123bimlive/lidar-intersection"
        title="Run your first corridor in 123BIMLive"
        text="Send us a LiDAR scan of one road and see every finding for it in your own project."
        secondary={{ to: "/contact", label: "Talk to our team" }}
      />
    </>
  );
}
